import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import packageInfo from "../../../package.json";

import { getState } from "../../state/store";
import { selectAppTypes } from "../../state/selectors";

import { appUserRoles, routePaths, strCode, appTypesNames, colors } from "../../consts";
import { appUserService, t } from "../../services";
import { consentUtil } from "../../utils";

import { FlexContainers, Logo, NavLink, SVG, WrapperContainer } from "../../components";
import { NavBarWrapper, VersionTitle, NavLinksWrapper } from "./styled";

const navigationLinks = [
  {
    to: routePaths.APPS,
    title: strCode.APPS_NAV_BTN,
    icon: <SVG.AppsNavIcon />,
    inactiveIcon: <SVG.AppsNavIcon color={colors.RAISIN_50} />
  },
  {
    to: routePaths.MARKETS,
    title: strCode.MARKETS_NAV_BTN,
    icon: <SVG.MarketsNavIcon />,
    inactiveIcon: <SVG.MarketsNavIcon color={colors.RAISIN_50} />
  },
  {
    to: routePaths.USERS,
    title: strCode.USERS_NAV_BTN,
    icon: <SVG.UsersNavIcon />,
    inactiveIcon: <SVG.UsersNavOutlinedIcon />
  },
  {
    to: routePaths.DASHBOARD,
    title: strCode.DASHBOARD_NAV_BTN,
    icon: <SVG.DashboardNavIcon />,
    inactiveIcon: <SVG.DashboardNavOutlinedIcon />
  },

  {
    to: routePaths.FLEET_MANAGEMENT,
    title: strCode.FLEET_MANAGEMENT_NAV_BTN,
    icon: <SVG.FleetManagementNavIcon />,
    inactiveIcon: <SVG.FleetManagementNavOutlinedIcon />
  },
  {
    to: routePaths.NOTIFICATIONS,
    title: strCode.NOTIFICATION_CENTER_NAV_BTN,
    icon: <SVG.NotificationsNavIcon />,
    inactiveIcon: <SVG.NotificationsNavOutlinedIcon />
  },
  {
    to: routePaths.HARDWARE,
    title: strCode.HARDWARE_NAV_BTN,
    icon: <SVG.HardwareNavIcon />,
    inactiveIcon: <SVG.HardwareNavOutlinedIcon />
  },
  // {
  //   to: routePaths.USER_INSIGHTS,
  //   title: strCode.USER_INSIGHTS_NAV_BTN,
  //   icon: <SVG.UserInsightsNavIcon />,
  //   inactiveIcon: <SVG.UserInsightsNavOutlinedIcon />
  // }
];

const NavBar = ({ onLogout = () => {} }) => {
  const userApps = selectAppTypes()(getState());

  const [isAppsTabLinkDisabled, setIsAppsTabLinkDisabled] = useState(false);

  const location = useLocation();

  const versionTitle = `${t(strCode.VERSION_TITLE)} ${packageInfo?.version}`;

  const selectedConsent = useSelector((store) => store.consents.selectedConsent);
  const consentElements = useSelector((store) => store.consents.consentElements);

  const isSameData = useMemo(() => {
    let isSame = false;

    if (consentElements?.length) {
      isSame =
        !consentUtil.checkIsSameConsentElements(selectedConsent?.items, consentElements) &&
        (location.pathname.includes(routePaths.CONSENT_ELEMENT_CREATE) ||
          location.pathname.includes(routePaths.CONSENT_ELEMENT_EDIT));
    }

    return isSame;
  }, [selectedConsent, consentElements, location]);

  const isOneAppTypeOnly = useMemo(() => {
    return userApps?.length === 2 && userApps?.some((type) => type === appTypesNames.allApps);
  }, [userApps]);

  useEffect(() => {
    const roles = appUserService.getRoles();
    setIsAppsTabLinkDisabled(
      !roles.includes(appUserRoles.Admin) &&
        location.pathname.startsWith(routePaths.APPS) &&
        isOneAppTypeOnly
    );
  }, [location.pathname]);

  const roles = appUserService.getRoles();
  const availablePages = appUserService.getAvailableRoutes(roles);

  return (
    <NavBarWrapper width={104} Container={FlexContainers.FlexColumnBetween} pv={24}>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} width={"100%"}>
        <Logo />

        <VersionTitle>{versionTitle}</VersionTitle>

        <NavLinksWrapper mt={12} width={"100%"} gap={4} Container={FlexContainers.FlexColumnStart}>
          {navigationLinks.map((link) => {
            return (
              availablePages.includes(link.to) && (
                <NavLink
                  key={link.title}
                  to={link.to}
                  title={link.title}
                  icon={link.icon}
                  inactiveIcon={link?.inactiveIcon}
                  disabled={(isAppsTabLinkDisabled && link.to === routePaths.APPS) || isSameData}
                />
              )
            );
          })}
        </NavLinksWrapper>
      </WrapperContainer>

      <NavLinksWrapper Container={FlexContainers.FlexColumn} width={"100%"} gap={2}>
        <NavLink
          title={strCode.LOGOUT_BTN}
          icon={<SVG.LogoutIcon />}
          inactiveIcon={<SVG.LogoutIcon color={colors.RAISIN_50} />}
          onClick={onLogout}
        />
      </NavLinksWrapper>
    </NavBarWrapper>
  );
};

NavBar.propTypes = {
  onLogout: PropTypes.func.isRequired
};

export default NavBar;
